// Host-only YouTube mount. Guests render nothing and never buffer media.
import React, { useEffect, useRef, useState } from 'react';
import { toast } from 'react-hot-toast';
import YouTubePlayer from './YouTubePlayer';
import usePlayerStore from '../stores/playerStore';
import useQueueStore from '../stores/queueStore';
import { useRoleStore } from '../stores/roleStore';
import { isEventHost } from '../utils/identity';
import { canAutostartQueueHead, shouldMountYouTubePlayer } from '../utils/playbackClock';
import { classifyYoutubeIframeError } from '../utils/youtubeIframeError';
import { notifyPlaybackSkipToast } from '../utils/playbackSkipToast';
import { reportPlaybackFailure } from '../services/playbackRepair';

export default function Player() {
  const [currentVideoId, setCurrentVideoId] = useState(null);
  const currentSong = usePlayerStore(state => state.currentSong);
  const setCurrentSong = usePlayerStore(state => state.setCurrentSong);
  const skipSong = usePlayerStore(state => state.skipSong);
  const notifyPlaybackEnded = usePlayerStore(state => state.notifyPlaybackEnded);
  const queue = useQueueStore(state => state.queue);
  const userId = useRoleStore(state => state.userId);
  const hostId = useRoleStore(state => state.hostId);
  const playerRecoveryStatus = usePlayerStore(state => state.playerRecoveryStatus);
  const isHost = isEventHost(userId, hostId);

  useEffect(() => {
    if (canAutostartQueueHead({
      isHost,
      recoveryStatus: playerRecoveryStatus,
      currentSong,
      queueLength: queue.length,
    })) {
      setCurrentSong(queue[0], { reason: 'song' });
    }
  }, [queue, currentSong, setCurrentSong, isHost, playerRecoveryStatus]);

  useEffect(() => {
    if (currentSong) {
      setCurrentVideoId(currentSong.id);
    }
  }, [currentSong]);

  const skipHandledVideoIdRef = useRef(null);

  useEffect(() => {
    skipHandledVideoIdRef.current = null;
  }, [currentVideoId]);

  const handleError = (event) => {
    if (!isHost) return;
    const videoId = currentVideoId;
    if (!videoId || skipHandledVideoIdRef.current === videoId) return;
    skipHandledVideoIdRef.current = videoId;
    const classified = classifyYoutubeIframeError(event?.data);
    notifyPlaybackSkipToast(toast, classified.hostMessage);
    if (classified.reportable) {
      const song = usePlayerStore.getState().currentSong;
      reportPlaybackFailure({
        reason: 'playback_error',
        reportable: true,
        iframeCode: classified.code,
        videoId,
        recordingId: song?.recordingId || null,
        providerAssetKey: song?.providerAssetKey || null,
      });
    }
    Promise.resolve(skipSong({ reason: 'playback_error' })).then((advanced) => {
      if (!advanced) {
        notifyPlaybackEnded();
      }
    });
  };

  if (!shouldMountYouTubePlayer(isHost) || !currentVideoId) {
    return null;
  }

  return (
    <div style={{ display: 'none' }}>
      <YouTubePlayer
        videoId={currentVideoId}
        onError={handleError}
      />
    </div>
  );
}
